import {
    FunctionComponent,
    useEffect,
    useState,
    useRef,
    RefObject,
    useCallback,
} from 'react';
import { Box, Input, useClipboard, useToast } from '@chakra-ui/react';
import { CopyIcon } from '@chakra-ui/icons';
import { truncStringPortion, viewOnEtherscan } from '../../imports/utils';
import { EtherscanIcon } from '../icons/icons';
import { AvailableNetworks } from '../../redux/common/types';
import { TwitterShareButton, TwitterIcon } from 'react-share';

type WalletAddressProps = {
    address: string;
    network: AvailableNetworks;
};

function useTruncatedAddress(
    ref: RefObject<HTMLInputElement>,
    address: string,
    charWidth = 20,
) {
    const [truncatedAddress, setTruncatedAddress] = useState<string>('');

    useEffect(() => {
        if (ref.current && address) {
            const width = ref.current.offsetWidth - 120;
            const maxChar = Math.floor(width / charWidth);
            const addressMiddle = Math.floor(address.length / 2);
            const toRemove = address.length - maxChar;
            const halfToRemove = Math.floor(toRemove / 2);

            setTruncatedAddress(
                toRemove > 0
                    ? truncStringPortion(
                          address,
                          addressMiddle - halfToRemove,
                          addressMiddle + halfToRemove,
                      )
                    : address,
            );
        }
    }, [ref]);

    return truncatedAddress;
}

const WalletAddress: FunctionComponent<WalletAddressProps> = ({
    address,
    network,
}) => {
    const { hasCopied, onCopy } = useClipboard(address);
    const toast = useToast();

    useEffect(() => {
        if (hasCopied) {
            toast({
                title: 'Indirizzo copiato',
                description: "L'indirizzo è stato copiato",
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        }
    }, [hasCopied]);

    const inputRef = useRef<HTMLInputElement>(null);

    const handleEtherscan = useCallback(
        () => window.open(viewOnEtherscan(network, 'address', address)),
        [address],
    );

    return (
        <Box w='100%' position='relative'>
            <Input
                ref={inputRef}
                value={useTruncatedAddress(inputRef, address, 10)}
                isReadOnly
                variant='filled'
                position='absolute'
            />

            <Box position='absolute' right='60px' top='11px' id='twitter'>
                <TwitterShareButton
                    url='https://bis-hub.com'
                    title={`Ho registrato il mio primo Wallet Ethereum. Questo è il mio nuovo address: ${address}`}
                    hashtags={['blockchain', 'IoV21']}
                >
                    <TwitterIcon round size={18} />
                </TwitterShareButton>
            </Box>

            <EtherscanIcon
                id='etherscan'
                onClick={handleEtherscan}
                position='absolute'
                right='36px'
                top='12px'
                cursor='pointer'
            />

            <CopyIcon
                onClick={onCopy}
                position='absolute'
                right='12px'
                top='12px'
                cursor='pointer'
            />
        </Box>
    );
};

export default WalletAddress;
